import type { LogItemType } from "@/types/common/logs";
import axios from "@/utils/axios";
import { useMutation, useQuery } from "@tanstack/react-query";

export interface LogFilters {
  relationship?: string;
  entity_id?: string;
  entity?: string;
  [key: string]: string | undefined;
}

export interface CauserData {
  id: number;
  name: string;
  last_name: string;
}

export interface LogData {
  id: number;
  description: string;
  event: string;
  causer: LogItemType["causer"];
  causer_id: string;
  causer_type: string;
  created_at: string;
  subject_type: string;
  subject_id: string;
  subject: LogItemType["subject"];
  properties: LogItemType["properties"];
}

/** one log */
export interface ApiLogSingleResponseType {
  data: LogData;
}

/** log list */
export interface ApiLogListResponseType {
  data: LogData[];
}

export type LogQueryOptions = {
  filters?: LogFilters;
  params?: Record<string, string | number | boolean>;
};

export const useGetLogs = (options?: LogQueryOptions) => {
  return useQuery<ApiLogListResponseType>({
    queryKey: ["admin-log-list", options],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const params: Record<string, string | number | boolean> = {};

      // filters
      if (options?.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params[`filter[${key}]`] = value;
          }
        });
      }

      // params
      if (options?.params) {
        Object.assign(params, options.params);
      }

      if (!params.per_page) {
        params.per_page = 100;
      }

      const { data } = await axios.get("admin/v1/log", {
        params,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      return data;
    },
  });
};

export const useGetLog = (
  id: number | string | undefined,
  filters?: LogFilters,
) => {
  return useQuery({
    queryKey: ["admin-log", id, filters],
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ApiLogSingleResponseType> => {
      const response = await axios.get(`admin/v1/log/${id}`, {
        params: filters,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      return response.data;
    },
  });
};

export const useDownloadLogs = (options: LogQueryOptions) => {
  return useMutation({
    mutationKey: ["logs-download"],
    mutationFn: async () => {
      // Generate filename based on entity type
      const entity = options?.filters?.entity || "logs";
      const entityId = options?.filters?.entity_id || "";
      const filename = entity === "tag" ? `tag-logs-${entityId}` : "logs";

      const params: Record<string, string | number | boolean> = {};

      // filters
      if (options?.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined) {
            params[`filter[${key}]`] = value;
          }
        });
      }

      // params
      if (options?.params) {
        Object.assign(params, options.params);
      }

      if (!params.per_page) {
        params.per_page = 100;
      }

      const { data } = await axios.post(
        "/admin/v1/log/download",
        { filename },
        {
          responseType: "blob",
          headers: {
            Accept: "text/csv",
            "Content-Type": "application/json",
          },
          params,
        },
      );

      const blob = new Blob([data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = globalThis.window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        filename.endsWith(".csv") ? filename : `${filename}.csv`,
      );
      document.body.appendChild(link);
      link.click();

      link.remove();
      globalThis.window.URL.revokeObjectURL(url);
    },
  });
};
